(() => {
  const init = () => {
    document.querySelectorAll('[data-section-type="product"][data-product-id]').forEach(root => {
      const button = root.querySelector('[data-cf-email-product]');
      if (!button || button.dataset.cfEmailProductReady === 'true') return;
      button.dataset.cfEmailProductReady = 'true';

      const productJsonEl = root.querySelector('[data-cf-page-product-json]');
      let productData = null;
      try { productData = JSON.parse(productJsonEl?.textContent || 'null'); }
      catch { productData = null; }
      if (!productData) return;

      const apiRoot = String(button.dataset.cfEmailApiRoot || '/apps/collective-projects').replace(/\/$/, '');
      const priceMode = button.dataset.cfEmailPriceMode === 'trade' ? 'trade' : 'retail';
      const currency = button.dataset.cfEmailCurrency || 'USD';
      let detailsPromise = null;
      let detailsVariantId = '';

      const decodeHtml = value => {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = String(value ?? '');
        return textarea.value;
      };

      const cleanText = value => decodeHtml(value).replace(/\s+/g, ' ').trim();

      const money = cents => {
        const amount = Number(cents || 0) / 100;
        try {
          return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
        } catch {
          return '$' + amount.toFixed(2);
        }
      };

      const activeVariant = () => {
        const id = root.dataset.cfActiveVariantId || productData.variantId;
        return (productData.variants || []).find(v => String(v.id) === String(id)) || productData.variants?.[0] || null;
      };

      const loadDetails = async () => {
        const variant = activeVariant();
        const variantId = String(variant?.id || productData.variantId || '');
        if (detailsPromise && detailsVariantId === variantId) return detailsPromise;
        detailsVariantId = variantId;
        detailsPromise = fetch(`${apiRoot}/project-details`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{
              productId: productData.id || '',
              handle: productData.handle || '',
              title: productData.title || '',
              vendor: productData.vendor || '',
              variantId: variant?.id || productData.variantId || '',
              sku: variant?.sku || '',
              dimensions: cleanText(variant?.dimensions || '')
            }]
          })
        }).then(async response => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok || !data?.ok) throw new Error(data?.error || `HTTP ${response.status}`);
          return { product: Array.isArray(data.products) ? data.products[0] || null : null, tariffSettings: data.tariffSettings || {} };
        }).catch(error => { detailsPromise = null; detailsVariantId = ''; throw error; });
        return detailsPromise;
      };

      loadDetails().then(({ product, tariffSettings }) => {
        const pricing = root.querySelector('[data-cf-pricing-wrap]');
        if (!pricing || !product?.tariffApplicable || tariffSettings?.enabled === false) return;
        let note = root.querySelector('[data-cf-tariff-note]');
        if (!note) {
          note = document.createElement('p');
          note.dataset.cfTariffNote = 'true';
          note.style.cssText = 'margin:0 0 8px;font-size:11px;line-height:1.55;color:rgba(20,20,20,.68)';
          const variantMessage = pricing.querySelector('small');
          if (variantMessage) pricing.insertBefore(note, variantMessage);
          else pricing.appendChild(note);
        }
        note.textContent = tariffSettings.disclaimer || `Please add a ${tariffSettings.percent || 10}% tariff surcharge to these prices.`;
      }).catch(error => console.warn('Tariff notice could not be loaded.', error));

      const pricingText = variant => {
        if (!variant) return '';
        if (variant.isOnSale && Number(variant.salePriceCents) > 0) {
          const parts = [];
          if (Number(variant.originalPriceCents) > 0) parts.push(`ORIGINAL PRICE: ${money(variant.originalPriceCents)}`);
          parts.push(`SALE PRICE: ${money(variant.salePriceCents)}`);
          return parts.join('\n');
        }
        const cents = priceMode === 'trade'
          ? Number(variant.tradeCents || variant.retailCents || 0)
          : Number(variant.retailCents || 0);
        return cents > 0 ? `${priceMode === 'trade' ? 'TRADE PRICE' : 'RETAIL PRICE'}: ${money(cents)}` : '';
      };

      const variantPrices = () => (productData.variants || []).map(variant => {
        const isOnSale = variant.isOnSale && Number(variant.salePriceCents) > 0;
        const priceCents = isOnSale
          ? Number(variant.salePriceCents || 0)
          : priceMode === 'trade'
            ? Number(variant.tradeCents || variant.retailCents || 0)
            : Number(variant.retailCents || 0);
        return {
          variantId: String(variant.id || ''),
          title: variant.title && variant.title !== 'Default Title' ? cleanText(variant.title) : 'Standard',
          priceCents,
          priceMode
        };
      }).filter(option => option.priceCents > 0);

      button.addEventListener('click', async () => {
        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'Preparing email...';

        try {
          if (!window.CFEmailComposer) throw new Error('Email composer is unavailable.');
          const { product: enriched } = await loadDetails();
          const variant = activeVariant();
          const product = {
            productId: enriched?.productId || productData.id || '',
            variantId: variant?.id || enriched?.variantId || productData.variantId || '',
            title: enriched?.title || productData.title || '',
            vendor: enriched?.vendor || productData.vendor || '',
            productUrl: enriched?.productUrl || new URL(variant?.url || productData.url || '', window.location.origin).href,
            tearsheetUrl: enriched?.tearsheetUrl || '',
            imageUrl: enriched?.imageUrl || variant?.image || productData.image || '',
            imageAlt: enriched?.imageAlt || variant?.imageAlt || productData.imageAlt || productData.title || '',
            imageDescription: enriched?.imageDescription || variant?.imageDescription || productData.imageDescription || '',
            model: enriched?.model || variant?.sku || '',
            dimensions: enriched?.dimensions || cleanText(variant?.dimensions || ''),
            minHeight: enriched?.minHeight || '',
            maxHeight: enriched?.maxHeight || '',
            weight: enriched?.weight || '',
            lightSource: enriched?.lightSource || '',
            totalWattage: enriched?.totalWattage || '',
            dimming: enriched?.dimming || '',
            certification: enriched?.certification || '',
            metafields: enriched?.metafields || {},
            materialGroups: enriched?.materialGroups || [],
            termsConditions: enriched?.termsConditions || {},
            tariffApplicable: enriched?.tariffApplicable === true,
            pricingText: pricingText(variant),
            priceCurrency: currency,
            variantPrices: variantPrices(),
            variantName: variant?.title && variant.title !== 'Default Title' ? cleanText(variant.title) : ''
          };

          window.CFEmailComposer.open({
            apiRoot,
            mode: 'product',
            title: `Email ${product.title || 'product'}`,
            payload: {
              project: { name: product.title || 'Product specification', totalPieces: 1 },
              products: [product]
            }
          });
        } catch (error) {
          console.error('Collective Form email product error:', error);
          alert(error?.message || 'The product email could not be prepared. Please try again.');
        } finally {
          button.disabled = false;
          button.textContent = originalText;
        }
      });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
